/**
 * A claim's own sending hooks.
 *
 * - `useSendPacketPreview` / `useSendPacket` - the manual "Send packet" on the
 *   claim detail page: the composer's send preview and send
 *   (`@/admin/hooks/use-composer`), on a claim. The system picks the template;
 *   the admin picks who.
 * - `useReissueUploadLink` - revoke the claim's upload link and send a new one.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  useComposerSend,
  useComposerSendPreview,
  type ComposerSendOptions,
} from '@/admin/hooks/use-composer'
import api from '@/admin/lib/api'

export type {
  ComposerRecipient as SendPacketRecipient,
  RecipientKind,
} from '@/admin/hooks/use-composer'

export function useSendPacketPreview(
  claimUuid: string | undefined,
  /** Compose a chosen letter instead of the one the claim's state resolves to. */
  templateId?: number | null,
) {
  return useComposerSendPreview('claim', claimUuid, templateId)
}

export function useSendPacket(
  claimUuid: string | undefined,
  options: ComposerSendOptions = {},
) {
  return useComposerSend('claim', claimUuid, options)
}

export interface ReissueChannelResult {
  /** 'sent' | 'failed' | 'skipped'. */
  status: string
  reason?: string
  communication_log_id?: number | null
}

export interface ReissueUploadLinkResponse {
  /** How many active upload sessions were revoked. */
  revoked: number
  /** The freshly-minted upload link every channel now carries. */
  upload_session_url: string
  /** Where the link was emailed. */
  recipient_email: string
  /** Email delivery outcome (email is always attempted). */
  email: ReissueChannelResult
  /** SMS delivery outcome, or null when SMS was not requested. */
  sms: ReissueChannelResult | null
}

export interface ReissueUploadLinkInput {
  /** Recipient email (defaults to the customer on file, admin-overridable). */
  recipient_email: string
  /** Also text the link (honoured only when the tenant has SMS enabled). */
  send_sms?: boolean
  /** Mobile number for the SMS (defaults to the customer's on file). */
  sms_contact?: string
}

/**
 * Revoke the claim's current evidence upload link, mint a fresh one, and
 * deliver it - the "the link reached the wrong person" case. Email is always
 * sent; SMS is an optional add-on. The shared-session resolver means every
 * channel reissues to this same new link.
 */
export function useReissueUploadLink(claimUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation<
    ReissueUploadLinkResponse,
    unknown,
    ReissueUploadLinkInput
  >({
    mutationFn: async (input) => {
      const res = await api.post(
        `/claims/claims/${claimUuid}/reissue-upload-link/`,
        input,
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['claim', claimUuid] })
      // WPS-142: the new link's email and the reminder it restarts show at
      // once, not on the next reload.
      qc.invalidateQueries({
        queryKey: ['communications-activity', 'record', 'claim', claimUuid],
      })
      qc.invalidateQueries({ queryKey: ['auto-reminder', 'claim', claimUuid] })
    },
  })
}
